import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { OrderPreemptModel } from './order-preempt.model';
import { OrderPreemptService } from './order-preempt.service';
import { DataTableComponent, PaginationEvent, TableColumn } from '@shared/components/data-table/data-table.component';
import { SearchFormComponent, SearchFormData } from '@shared/components/search-form/search-form.component';
import { ActionButton } from '@shared/components/status-filter/status-filter.component';

@Component({
  selector: 'app-order-preempt',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DataTableComponent,
    SearchFormComponent,
  ],
  templateUrl: './order-preempt.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderPreemptComponent implements OnInit {
  private orderPreemptService = inject(OrderPreemptService);

  orders: OrderPreemptModel[] = [];
  selectedOrders: OrderPreemptModel[] = [];
  loading = false;
  totalRecords = 0;
  first = 0;
  rows = 20;

  // Status filter options
  statusOptions = [
    { key: 'all', label: 'All', active: true },
    { key: 'pending', label: 'Pending Preempt' },
    { key: 'success', label: 'Preempt Success' },
    { key: 'failed', label: 'Preempt Failed' }
  ];
  activeStatus = 'all';

  // Dropdown options
  channels = ['Facebook', 'Shopee', 'Tmall'];
  stores = ['Happy Selection Online Store', 'Colorful Good Goods Warehouse', 'Trend Station'];
  preemptStatuses = ['All Preempt Success', 'All Preempt Failed', 'Partial Preempt Success'];

  // Button configurations
  primaryButton: ActionButton = {
    label: 'Retry Preempt',
    severity: 'info',
    outlined: false,
    disabled: false,
    show: true
  };

  secondaryButton: ActionButton = {
    label: 'Export Data',
    severity: 'info',
    outlined: true,
    disabled: false,
    show: true
  };

  // Table columns
  columns: TableColumn[] = [
    { label: 'Third Party Order Number', prop: 'thirdPartyOrderNumber', width: 150 },
    { label: 'Internal Order Number', prop: 'internalOrderNumber', width: 150 },
    { label: 'Preempt Code', prop: 'preemptCode', width: 150 },
    { label: 'Preempt Status', prop: 'preemptStatus', width: 120 },
    { label: 'Product Types', prop: 'productTypes', width: 120 },
    { label: 'Total Product Lines', prop: 'totalProductLines', width: 120 },
    { label: 'Pending Preempt', prop: 'pendingPreempt', width: 120 },
    { label: 'Preempt Success', prop: 'preemptSuccess', width: 120 },
    { label: 'Sales Channel', prop: 'salesChannel', width: 120 },
    { label: 'Preempt Failed', prop: 'preemptFailed', width: 120 },
    { label: 'Sales Store', prop: 'salesStore', width: 120 },
    { label: 'Third Party Order Time', prop: 'thirdPartyOrderTime', width: 150 },
    { label: 'Internal Order Creation Time', prop: 'internalOrderCreationTime', width: 150 },
    { label: 'Expected Shipment Time', prop: 'expectedShipmentTime', width: 150 }
  ];

  ngOnInit(): void {
    this.loadOrders();
  }

  loadOrders(): void {
    this.loading = true;
    const searchParams = {
      status: this.activeStatus,
      first: this.first,
      rows: this.rows
    };

    this.orderPreemptService.getOrders(searchParams).subscribe({
      next: (response) => {
        this.orders = response.data;
        this.totalRecords = response.total;
        this.loading = false;
      },
      error: (error) => {
        console.error('Failed to load orders:', error);
        this.loading = false;
      }
    });
  }

  onSearch(searchData: SearchFormData): void {
    this.first = 0;
    this.loadOrders();
  }

  onReset(): void {
    this.activeStatus = 'all';
    this.first = 0;
    this.loadOrders();
  }

  onStatusChange(status: string): void {
    this.activeStatus = status;
    this.first = 0;
    this.loadOrders();
  }

  onPageChange(event: PaginationEvent): void {
    this.first = event.first || 0;
    this.rows = event.rows || 20;
    this.loadOrders();
  }

  onSelectionChange(event: any): void {
    this.selectedOrders = event;
  }

  retryPreempt(): void {
    if (this.selectedOrders.length === 0) {
      console.warn('Please select orders to retry preempt');
      return;
    }

    const orderIds = this.selectedOrders.map(order => order.id);
    this.orderPreemptService.retryPreempt(orderIds).subscribe({
      next: () => {
        console.log('Preempt retry initiated successfully');
        this.selectedOrders = [];
        this.loadOrders();
      },
      error: (error) => {
        console.error('Failed to retry preempt:', error);
      }
    });
  }

  exportData(): void {
    this.orderPreemptService.exportOrders({}).subscribe({
      next: (response) => {
        // Handle file download
        const blob = new Blob([response], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'order-preempt-data.csv';
        link.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Failed to export data:', error);
      }
    });
  }

  refreshData(): void {
    this.loadOrders();
  }

  getStatusSeverity(status: string): string {
    switch (status) {
      case 'All Preempt Success':
        return 'success';
      case 'All Preempt Failed':
        return 'danger';
      case 'Partial Preempt Success':
        return 'warning';
      default:
        return 'info';
    }
  }
} 